/**
 * Account Settings Page — iter90 / Issue 13 (+ iter95 — Issue 19)
 *
 * Per-user settings, distinct from the dealer-level Settings page:
 *   1. Profile — display name + email (read-only; admin must change)
 *   2. Password — current / new / confirm + Save button
 *   3. Notifications — per-user toggles (new lead, hot return, daily digest)
 *
 * The route ?page=account-settings used to fall through to the
 * dealer overview page because no case existed in App.js' switch
 * statement. Wiring + a menu entry in the avatar dropdown were
 * added in the same iteration.
 */
import { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useToast } from "../contexts/ToastContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { User, Lock, Bell, AlertCircle, Mail, Flame, Calendar as CalendarIcon } from "lucide-react";

const AccountSettingsPage = () => {
  const { user, apiFetch } = useAuth();
  const toast = useToast();

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // iter95 — Issue 19: notification preferences. Defaults ON until backend confirms.
  const [notifs, setNotifs] = useState({
    notif_new_lead: true,
    notif_hot_return: true,
    notif_daily_digest: true,
  });
  const [notifsLoading, setNotifsLoading] = useState(true);
  const [notifSaving, setNotifSaving] = useState(null); // key being saved
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await apiFetch("/auth/notifications", { method: "GET" });
        if (alive && res?.data) {
          setNotifs({
            notif_new_lead: res.data.notif_new_lead !== false,
            notif_hot_return: res.data.notif_hot_return !== false,
            notif_daily_digest: res.data.notif_daily_digest !== false,
          });
        }
      } catch (_e) {
        // keep defaults
      } finally {
        if (alive) setNotifsLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [apiFetch]);

  const toggleNotif = async (key) => {
    const next = !notifs[key];
    setNotifs(prev => ({ ...prev, [key]: next }));
    setNotifSaving(key);
    try {
      await apiFetch("/auth/notifications", {
        method: "PUT",
        data: { [key]: next },
      });
      toast.success?.("Notification preference saved");
    } catch (err) {
      // revert on failure
      setNotifs(prev => ({ ...prev, [key]: !next }));
      toast.error?.(err?.response?.data?.detail || "Could not save preference");
    } finally {
      setNotifSaving(null);
    }
  };

  const displayName = [user?.first_name, user?.last_name].filter(Boolean).join(" ").trim() || "—";

  const reset = () => {
    setCurrentPassword("");
    setNewPassword("");
    setConfirmPassword("");
    setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!currentPassword) return setError("Please enter your current password.");
    if (!newPassword) return setError("Please choose a new password.");
    if (newPassword.length < 8) return setError("New password must be at least 8 characters.");
    if (newPassword === currentPassword) return setError("New password must be different from your current password.");
    if (newPassword !== confirmPassword) return setError("New password and confirmation do not match.");

    setSaving(true);
    try {
      await apiFetch("/auth/change-password", {
        method: "POST",
        data: { current_password: currentPassword, new_password: newPassword },
      });
      toast.success?.("Password updated successfully.");
      reset();
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to update password. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-6 max-w-3xl mx-auto" data-testid="account-settings-page">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Account Settings</h1>
        <p className="text-gray-500 text-sm mt-0.5">
          Manage your personal account. Dealership-level settings live under{" "}
          <span className="font-medium text-gray-700">Settings</span> in the sidebar.
        </p>
      </div>

      {/* Profile */}
      <Card className="mb-6" data-testid="account-profile-card">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <User className="w-5 h-5 text-blue-500" />
            <CardTitle className="text-lg">Profile</CardTitle>
          </div>
          <CardDescription>Identity and contact information on file.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
              Display name
            </label>
            <input
              type="text"
              value={displayName}
              readOnly
              data-testid="account-display-name"
              className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm bg-gray-50 text-gray-700 cursor-not-allowed"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
              Email address
            </label>
            <input
              type="email"
              value={user?.email || ""}
              readOnly
              data-testid="account-email"
              className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm bg-gray-50 text-gray-700 cursor-not-allowed"
            />
            <p className="text-xs text-gray-500 mt-1.5">
              To change your email address, please contact your account administrator.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Password */}
      <Card className="mb-6" data-testid="account-password-card">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <Lock className="w-5 h-5 text-purple-500" />
            <CardTitle className="text-lg">Password</CardTitle>
          </div>
          <CardDescription>
            Use a unique password with at least 8 characters.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="change-password-form">
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                Current password
              </label>
              <input
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                autoComplete="current-password"
                data-testid="current-password-input"
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                disabled={saving}
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                  New password
                </label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  autoComplete="new-password"
                  data-testid="new-password-input"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                  disabled={saving}
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                  Confirm new password
                </label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  autoComplete="new-password"
                  data-testid="confirm-password-input"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                  disabled={saving}
                />
              </div>
            </div>
            {error && (
              <div className="flex items-start gap-2 px-3 py-2 bg-red-50 border border-red-200 rounded-md text-sm text-red-700" data-testid="change-password-error">
                <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}
            <div className="flex justify-end">
              <button
                type="submit"
                disabled={saving}
                data-testid="change-password-save-btn"
                className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded-md hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {saving && <span className="inline-block w-3 h-3 border-2 border-white/40 border-t-white rounded-full animate-spin" />}
                {saving ? "Saving…" : "Save Password"}
              </button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* iter95 — Issue 19: Real notification toggles */}
      <Card data-testid="account-notifications-card">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <Bell className="w-5 h-5 text-amber-500" />
            <CardTitle className="text-lg">Notifications</CardTitle>
          </div>
          <CardDescription>
            Choose which lead activity emails you want delivered to <span className="font-medium text-gray-700">{user?.email}</span>.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ul className="divide-y divide-gray-100" data-testid="notification-options-list">
            <NotificationToggleRow
              icon={<Mail className="w-4 h-4 text-blue-500" />}
              title="New verified lead"
              description="Email sent immediately when a visitor verifies their phone and unlocks a price. Includes name, phone, and vehicle interest."
              testId="notif-new-lead"
              checked={notifs.notif_new_lead}
              onToggle={() => toggleNotif("notif_new_lead")}
              loading={notifsLoading || notifSaving === "notif_new_lead"}
            />
            <NotificationToggleRow
              icon={<Flame className="w-4 h-4 text-orange-500" />}
              title="Hot Return alert"
              description="Email sent when a previously verified shopper returns to your site — a high-intent opportunity for follow-up."
              testId="notif-hot-return"
              checked={notifs.notif_hot_return}
              onToggle={() => toggleNotif("notif_hot_return")}
              loading={notifsLoading || notifSaving === "notif_hot_return"}
            />
            <NotificationToggleRow
              icon={<CalendarIcon className="w-4 h-4 text-purple-500" />}
              title="Daily lead summary digest"
              description="Morning email with yesterday's lead count, top vehicles viewed, and new verified leads."
              testId="notif-daily-digest"
              checked={notifs.notif_daily_digest}
              onToggle={() => toggleNotif("notif_daily_digest")}
              loading={notifsLoading || notifSaving === "notif_daily_digest"}
            />
          </ul>
          <p className="text-xs text-gray-400 mt-4 leading-relaxed">
            Notifications are enabled by default for new accounts. Toggle any option off to stop receiving that email.
            For dealership-wide CRM webhook delivery, see <span className="font-medium text-gray-600">Settings &rarr; Integrations</span>.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

const NotificationToggleRow = ({ icon, title, description, testId, checked, onToggle, loading }) => (
  <li className="py-3 flex items-start gap-3" data-testid={`${testId}-row`}>
    <div className="mt-1 flex-shrink-0">{icon}</div>
    <div className="flex-1 min-w-0">
      <p className="text-sm font-semibold text-gray-900 m-0">{title}</p>
      <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">{description}</p>
    </div>
    <button
      type="button"
      onClick={onToggle}
      disabled={loading}
      data-testid={`${testId}-toggle`}
      aria-label={`Toggle ${title}`}
      aria-pressed={checked}
      className={`relative inline-flex h-5 w-9 flex-shrink-0 items-center rounded-full transition-colors disabled:opacity-50 ${
        checked ? "bg-purple-600" : "bg-gray-300"
      }`}
    >
      <span
        className="inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform"
        style={{ transform: checked ? "translateX(20px)" : "translateX(2px)" }}
      />
    </button>
  </li>
);

export default AccountSettingsPage;
